/**
 * Feature flags with per-browser dev overrides (ITDEVELOP-3119).
 *
 * A flag's effective value resolves as: localStorage override (dev tools only)
 * → build-time env default. Overrides are honored ONLY when dev tools are
 * enabled — locally under `vite dev`, or in images built with
 * `VITE_DEV_TOOLS=true` (the `build-dev:portal-vue` CI job). Staging and
 * production builds set neither, so overrides left in localStorage are inert
 * there.
 *
 */

export type FeatureFlagName = 'slaOrgWizard';

export interface FeatureFlagDefinition {
    label: string;
    description: string;
    envVar: keyof ImportMetaEnv;
    storageKey: string;
}

export const FEATURE_FLAGS: Record<FeatureFlagName, FeatureFlagDefinition> = {
    slaOrgWizard: {
        label: 'Organization wizard (SLA upgrade)',
        description: 'New 4-step Create/Edit Organization wizard (ITDEVELOP-3227) instead of the 5-step creation process.',
        envVar: 'VITE_FEATURE_SLA_ORG_WIZARD',
        storageKey: 'dev:feature:sla-org-wizard',
    },
};

export function isDevToolsEnabled(): boolean {
    return Boolean(import.meta.env.DEV) || import.meta.env.VITE_DEV_TOOLS === 'true';
}

export function getFeatureDefault(name: FeatureFlagName): boolean {
    return import.meta.env[FEATURE_FLAGS[name].envVar] === 'true';
}

/** The active per-browser override, or null when none is set (or dev tools are off). */
export function getFeatureOverride(name: FeatureFlagName): boolean | null {
    if (!isDevToolsEnabled()) {
        return null;
    }
    try {
        const stored = localStorage.getItem(FEATURE_FLAGS[name].storageKey);
        if (stored === 'true') {
            return true;
        }
        if (stored === 'false') {
            return false;
        }
    } catch {
        // localStorage unavailable (e.g. private mode) — behave as if unset.
    }
    return null;
}

/** Set (true/false) or clear (null) the per-browser override. No-op when dev tools are off. */
export function setFeatureOverride(name: FeatureFlagName, value: boolean | null): void {
    if (!isDevToolsEnabled()) {
        return;
    }
    try {
        const { storageKey } = FEATURE_FLAGS[name];
        if (value === null) {
            localStorage.removeItem(storageKey);
        } else {
            localStorage.setItem(storageKey, String(value));
        }
    } catch {
        // localStorage unavailable — nothing to persist.
    }
}

export function isFeatureEnabled(name: FeatureFlagName): boolean {
    return getFeatureOverride(name) ?? getFeatureDefault(name);
}
